import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { getHeaders } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

interface Hotel {
  id?: string;
  propertyId?: string; // PMS property id
  propertyID?: string;
  propertyName?: string;
  [key: string]: any;
}

interface HotelsCardProps {
  propertyId: string; // local property id
  currentIntegration?: any | null;
}

const HotelsCard: React.FC<HotelsCardProps> = ({ propertyId, currentIntegration }) => {
  const { toast } = useToast();
  const [hotels, setHotels] = useState<Hotel[]>([]);
  const [loading, setLoading] = useState(false);
  const [setting, setSetting] = useState(false);
  const [selectedHotelId, setSelectedHotelId] = useState<string | null>(null);

  // derive any existing external mapping stored on the integration
  const existingExternalId = (
    currentIntegration && currentIntegration.externalId 
  )?.toString();

  const existingExternalName = (
    currentIntegration && currentIntegration.externalPropertyName 
  ) || "";

  useEffect(() => {
    if (existingExternalId) setSelectedHotelId(existingExternalId);
  }, [existingExternalId]);

  const fetchHotels = async () => {
    if (!currentIntegration) {
      toast({ title: "No PMS integration", description: "Connect a PMS integration before fetching hotels.", variant: "destructive" });
      return;
    }

    try {
      setLoading(true);
      const baseHeaders = await getHeaders();
      const headers = { ...baseHeaders, "Content-Type": "application/json" } as HeadersInit;

      const integrationId = currentIntegration.integrationId || currentIntegration.id || null;
      const providerName = (currentIntegration.name || currentIntegration.provider || "").toString().toLowerCase().replace(/\s+/g, "");

      const body = JSON.stringify({ propertyId, integrationId, pmsProvider: providerName });

      const resp = await fetch(`${import.meta.env.VITE_API_URL}/pms/hotels`, {
        method: "POST",
        mode: "cors",
        headers,
        body,
      });

      if (!resp.ok) {
        throw new Error(`Failed to fetch hotels: ${resp.status}`);
      }

      const data = await resp.json();
      // Accept various shapes
      let list: Hotel[] = [];
      if (Array.isArray(data)) list = data;
      else if (data && data.hotels) list = data.hotels;
      else if (data && data.data) list = data.data;
      else if (data && data.body) list = data.body;

      setHotels(list || []);
      console.debug("Hotels loaded:", list);
      toast({ title: "Hotels loaded" });
    } catch (error) {
      console.error("Error fetching hotels", error);
      toast({ title: "Error fetching hotels", description: String(error), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const setPmsProperty = async () => {
    if (!selectedHotelId) {
      toast({ title: "Select a hotel", description: "Choose a hotel before setting PMS property ID", variant: "destructive" });
      return;
    }
    if (!currentIntegration) {
      toast({ title: "No PMS integration", description: "Connect a PMS integration before setting property ID.", variant: "destructive" });
      return;
    }

    try {
      setSetting(true);
      const baseHeaders = await getHeaders();
      const headers = { ...baseHeaders, "Content-Type": "application/json" } as HeadersInit;

      const integrationId = currentIntegration.integrationId || currentIntegration.id || null;
      const providerName = (currentIntegration.name || currentIntegration.provider || "").toString();

      const selectedHotel = hotels.find((h) => ((h.propertyID || h.propertyId || h.id) || '').toString() === selectedHotelId);
      const externalPropertyName = selectedHotel ? (selectedHotel.propertyName || selectedHotel.name || '') : '';

      const bodyPayload = {
        propertyId,
        integrationId,
        externalPropertyId: selectedHotelId,
        externalPropertyName,
        external_property_name: externalPropertyName
      };

      const body = JSON.stringify(bodyPayload);
      console.debug("Updating integration - request body:", bodyPayload);

      const resp = await fetch(`${import.meta.env.VITE_API_URL}/integrations/updatePropertyIntegration`, {
        method: "POST",
        mode: "cors",
        headers,
        body,
      });

      if (!resp.ok) {
        const errBody = await resp.text();
        throw new Error(errBody || `Status ${resp.status}`);
      }

      toast({ title: "PMS property set", description: "Selected PMS property has been associated" });
    } catch (error) {
      console.error("Error setting PMS property id", error);
      toast({ title: "Error setting PMS property id", description: String(error), variant: "destructive" });
    } finally {
      setSetting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>PMS Hotels</CardTitle>
        <CardDescription>Retrieve hotels from the connected PMS and map to this property</CardDescription>
      </CardHeader>

      <CardContent>
        {/* Show existing mapping prominently (not collapsible) */}
        {(existingExternalId || existingExternalName) && (
          <div className="mb-3 rounded-md bg-muted p-3">
            <div className="text-sm font-medium">Mapped PMS Property</div>
            <div className="text-sm">{existingExternalName || "—"}</div>
            <div className="text-sm text-muted-foreground">PMS ID: {existingExternalId || "—"}</div>
          </div>
        )}

        <div className="flex items-center justify-between mb-3">
          <div className="flex space-x-2">
            <Button size="sm" onClick={fetchHotels} disabled={loading}>
              {loading ? "Loading..." : "Fetch Hotels"}
            </Button>
            <Button size="sm" onClick={setPmsProperty} disabled={setting || !selectedHotelId}>
              {setting ? "Setting..." : "Set PMS Property"}
            </Button>
          </div>
        </div>

        {hotels.length === 0 ? (
          <p className="text-sm text-muted-foreground">No hotels loaded.</p>
        ) : (
          <div className="space-y-2">
            <RadioGroup value={selectedHotelId || undefined} onValueChange={(v) => setSelectedHotelId(v)}>
              {hotels.map((h, idx) => {
                const externalId = (h.propertyID || h.propertyId || h.id || idx).toString();
                const displayName = h.propertyName || h.name || `Hotel ${idx + 1}`;
                const displayPmsId = h.propertyID || h.propertyId || h.id || "-";
                return (
                  <div key={externalId} className="flex items-center justify-between p-2 rounded hover:bg-muted">
                    <div>
                      <div className="font-medium">{displayName}</div>
                      <div className="text-sm text-muted-foreground">PMS ID: {displayPmsId}</div>
                    </div>
                    <RadioGroupItem value={externalId} />
                  </div>
                );
              })}
            </RadioGroup>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default HotelsCard;


